import type { CapabilityType, Entitlements } from '../../access/models/Entitlements';
import type { ExportReportType } from '../models/ExportRequest';
import { ProductAccessService } from '../../access/services/ProductAccessService';

const REPORT_CAPABILITY: Partial<Record<ExportReportType, CapabilityType>> = {
  'genogram.clinical_case': 'clinical.report.generate',
  'genogram.health_risk_summary': 'clinical.health.view',
  'genogram.legal_forensic': 'clinical.export.court',
  'genogram.relationship_dynamics': 'clinical.pattern.detect',
  'genogram.genealogical_historical': 'clinical.report.generate',
  'genogram.custom_analytical': 'clinical.report.generate',
  'genogram.session_progress_snapshot': 'clinical.report.generate',
  'genogram.anonymized_teaching': 'clinical.report.generate',
};

export function requiredClinicalCapabilityForReport(
  reportType?: ExportReportType,
): CapabilityType | null {
  if (!reportType) return null;
  return REPORT_CAPABILITY[reportType] ?? null;
}

export function isGenogramReportPlanBlocked(planType: Entitlements['planType'] | undefined): boolean {
  return (planType ?? 'free') === 'free';
}

export interface ReportExportAccess {
  allowed: boolean;
  requiredCapability: CapabilityType | null;
  requiredAddOn: null;
}

function isGenogramReportBlocked(entitlements: Entitlements | undefined): boolean {
  if (entitlements?.commercial) return false;
  return isGenogramReportPlanBlocked(entitlements?.planType);
}

export function getReportExportAccess(
  entitlements: Entitlements | undefined,
  reportType: ExportReportType,
): ReportExportAccess {
  const requiredCapability = requiredClinicalCapabilityForReport(reportType);
  if (!requiredCapability) {
    return { allowed: true, requiredCapability: null, requiredAddOn: null };
  }

  if (entitlements?.commercial) {
    const descriptor = new ProductAccessService().describe({
      entitlements,
      capability: requiredCapability,
      productContext: 'clinical',
    });
    return {
      allowed: descriptor.allowed,
      requiredCapability: descriptor.allowed ? null : requiredCapability,
      requiredAddOn: null,
    };
  }

  const blocked = isGenogramReportBlocked(entitlements);
  return {
    allowed: !blocked,
    requiredCapability: blocked ? requiredCapability : null,
    requiredAddOn: null,
  };
}
